/* VIVA63 — generic scroll-snap carousel controller.
   Root: [data-viva-cc]  Track: [data-viva-cc-track]  Cards: [data-viva-cc-card]
   Prev/Next: [data-viva-cc-prev] / [data-viva-cc-next]  Dots: [data-viva-cc-dot]
   Emits 'viva:carousel:change' ({index}) on the root. */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.vivaCcReady) return;
    root.dataset.vivaCcReady = '1';

    var track = root.querySelector('[data-viva-cc-track]');
    if (!track) return;
    var cards = Array.prototype.slice.call(track.querySelectorAll('[data-viva-cc-card]'));
    var prev = root.querySelector('[data-viva-cc-prev]');
    var next = root.querySelector('[data-viva-cc-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-viva-cc-dot]'));
    var autoplay = root.dataset.vivaCcAutoplay === 'true';
    var interval = parseInt(root.dataset.vivaCcInterval, 10) || 5000;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (cards.length < 2) {
      if (prev) {
        prev.disabled = true;
        prev.setAttribute('aria-disabled', 'true');
      }
      if (next) {
        next.disabled = true;
        next.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    var current = 0;
    var raf = 0;
    var autoplayTimer = 0;
    var autoplayPaused = false;
    var autoplayInView = false;
    var hoverPaused = false;

    root.style.setProperty('--viva-cc-autoplay-duration', interval + 'ms');
    if (autoplay) track.scrollLeft = 0;

    function scrollPaddingLeft() {
      var v = parseFloat(getComputedStyle(track).scrollPaddingLeft);
      if (isNaN(v)) v = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      return v;
    }

    function offsetOf(i) {
      if (i === cards.length - 1) {
        return cards[i].offsetLeft - ((track.clientWidth - cards[i].offsetWidth) / 2);
      }
      return cards[i].offsetLeft - scrollPaddingLeft();
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function nearestIndex() {
      var x = track.scrollLeft;
      var best = 0;
      var bestD = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var d = Math.abs(offsetOf(i) - x);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    }

    function render() {
      var atEnd = track.scrollLeft >= maxScroll() - 2;
      var prevDisabled = current <= 0;
      var nextDisabled = current >= cards.length - 1 || atEnd;
      if (prev) {
        prev.disabled = prevDisabled;
        prev.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
      }
      if (next) {
        next.disabled = nextDisabled;
        next.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      }
      dots.forEach(function (d, i) {
        var on = i === current;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      cards.forEach(function (c, i) {
        c.classList.toggle('is-active', i === current);
      });
      if (autoplay) {
        var activeHovered = cards[current] && cards[current].matches(':hover');
        if (hoverPaused !== activeHovered) {
          hoverPaused = activeHovered;
          syncAutoplayPause();
        }
      }
    }

    function restartProgress() {
      root.classList.remove('is-autoplaying');
      void root.offsetWidth;
      if (autoplay && !reduceMotion) root.classList.add('is-autoplaying');
    }

    function stopAutoplay() {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = 0;
    }

    function scheduleAutoplay() {
      stopAutoplay();
      if (!autoplay || reduceMotion || !autoplayInView || autoplayPaused || document.hidden) return;
      restartProgress();
      autoplayTimer = window.setTimeout(function () {
        setIndex((current + 1) % cards.length);
        scheduleAutoplay();
      }, interval);
    }

    function syncAutoplayPause() {
      var paused = hoverPaused;
      if (autoplayPaused === paused) return;
      autoplayPaused = paused;
      root.classList.toggle('is-autoplay-paused', paused);
      if (paused) stopAutoplay();
      else scheduleAutoplay();
    }

    function setAutoplayInView(inView) {
      if (autoplayInView === inView) return;
      autoplayInView = inView;
      if (inView) {
        hoverPaused = Boolean(cards[current] && cards[current].matches(':hover'));
        syncAutoplayPause();
        scheduleAutoplay();
      } else {
        stopAutoplay();
        root.classList.remove('is-autoplaying');
      }
    }

    function setIndex(i, behavior) {
      i = Math.max(0, Math.min(cards.length - 1, i));
      var target = Math.min(offsetOf(i), maxScroll());
      track.scrollTo({ left: target, behavior: behavior || 'smooth' });
      if (i !== current) {
        current = i;
        root.dispatchEvent(new CustomEvent('viva:carousel:change', { detail: { index: i } }));
      }
      render();
    }

    function onScroll() {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = 0;
        var i = nearestIndex();
        if (i !== current) {
          current = i;
          root.dispatchEvent(new CustomEvent('viva:carousel:change', { detail: { index: i } }));
        }
        render();
      });
    }

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (prev) prev.addEventListener('click', function () { setIndex(current - 1); scheduleAutoplay(); });
    if (next) next.addEventListener('click', function () { setIndex((current + 1) % cards.length); scheduleAutoplay(); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        setIndex(parseInt(d.dataset.index, 10) || 0);
        scheduleAutoplay();
      });
    });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); setIndex(current + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); setIndex(current - 1); }
    });
    if (!track.hasAttribute('tabindex')) track.setAttribute('tabindex', '0');

    if (autoplay) {
      cards.forEach(function (card) {
        card.addEventListener('mouseenter', function () {
          hoverPaused = card.classList.contains('is-active');
          syncAutoplayPause();
        });
        card.addEventListener('mouseleave', function () {
          hoverPaused = false;
          syncAutoplayPause();
        });
      });
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stopAutoplay();
        else scheduleAutoplay();
      });
      if ('IntersectionObserver' in window) {
        var autoplayObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.target === root) setAutoplayInView(entry.isIntersecting);
          });
        }, { threshold: 0.15 });
        autoplayObserver.observe(root);
      } else {
        setAutoplayInView(true);
      }
    }

    root.vivaCarousel = { setIndex: setIndex, getIndex: function () { return current; } };
    render();
  }

  function boot() {
    document.querySelectorAll('[data-viva-cc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
